import React from 'react';
import { Droppable, DroppableProvided, DroppableStateSnapshot } from '@campfire/draggable-list';
import { FormControlLabel, List, Radio } from '@material-ui/core';
import { BuilderContext } from './types';

interface DropItemTriggerProps {
  optionId: string;
  name: string;
}

export function DropItemTrigger({ optionId, name }: DropItemTriggerProps) {
  const builderContext = React.useContext(BuilderContext);

  if (!builderContext) {
    // No builder context - render static preview
    return <FormControlLabel control={<Radio disabled />} label={name} />;
  }

  const { items, draggingId, renderItem, itemLabel } = builderContext;
  const triggerData = items.byTrigger[optionId] || {};
  const triggerItemIds = triggerData.triggerItemIds || [];
  const parentItemId = triggerData.taskItemId || triggerData.formItemId || '';
  const canDrop = Boolean(items.byTriggerOption[draggingId]);
  const droppableType = canDrop ? 'items' : undefined;

  return (
    <Droppable droppableId={optionId} isDropDisabled={false} type={droppableType}>
      {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
        const style = {
          minHeight: '80px',
          maxWidth: '100%',
          borderRadius: '6px',
          color: '#979797',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          border: '2px dashed',
          borderColor: '#979797',
          paddingLeft: '8px',
          paddingRight: '8px',
        };

        const isSelfContaining = snapshot.draggingOverWith === parentItemId;

        const updatedStyle =
          snapshot.isDraggingOver && !isSelfContaining
            ? {
                pointerEvents: 'none',
                ...style,
                borderColor: 'lightblue',
              }
            : style;

        return (
          <div ref={provided.innerRef}>
            <FormControlLabel control={<Radio disabled />} label={name} />
            {triggerItemIds && triggerItemIds.length > 0 ? (
              <List style={updatedStyle as React.CSSProperties}>
                {triggerItemIds.map((itemId, itemIndex: number) => (
                  <React.Fragment key={itemId}>{renderItem(itemId, itemIndex)}</React.Fragment>
                ))}
                {provided.placeholder}
              </List>
            ) : (
              <div style={updatedStyle as React.CSSProperties}>
                {`Drag and drop ${itemLabel} here to be shown when '${name}' is selected`}
              </div>
            )}
          </div>
        );
      }}
    </Droppable>
  );
}
